import qrcode from 'qrcode-generator';
import logoUrl from '../assets/blast-qr-logo.png';

/** Booth-printable by default — a 512px PNG survives being blown up on a poster. */
export const QR_EXPORT_SIZE = 512;

/** Quiet zone, in modules. The spec's minimum is 4; below it scanners struggle. */
const MARGIN_MODULES = 4;

/** Logo edge as a fraction of the QR edge — the ratio Blast Rails composites at. */
const LOGO_RATIO = 0.2;

let logoPromise: Promise<HTMLImageElement> | null = null;

/** Decoded once per page; every QR draw reuses the same image. */
const loadLogo = (): Promise<HTMLImageElement> => {
  if (!logoPromise) {
    logoPromise = new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Could not load the Blast QR logo'));
      img.src = logoUrl;
    });
  }
  return logoPromise;
};

/**
 * Draw a QR for `text` onto `canvas` at `size` css pixels, with the Blast logo
 * centered over it.
 *
 * Error correction H (~30% recovery) is what makes the centered logo safe: it
 * occludes roughly a quarter of the module area, which is past what M can lose.
 * Resolves once the logo is on the canvas, so callers exporting a PNG never
 * capture a half-drawn code.
 */
export const drawQrToCanvas = async (
  canvas: HTMLCanvasElement,
  text: string,
  size: number,
): Promise<void> => {
  const qr = qrcode(0, 'H');
  qr.addData(text);
  qr.make();

  const moduleCount = qr.getModuleCount();
  const total = moduleCount + MARGIN_MODULES * 2;
  // Integer module size keeps edges crisp; the remainder becomes extra quiet zone.
  const moduleSize = Math.max(1, Math.floor(size / total));
  const drawn = moduleSize * total;
  const offset = Math.floor((size - drawn) / 2);

  canvas.width = size;
  canvas.height = size;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, size, size);
  ctx.fillStyle = '#000000';
  for (let row = 0; row < moduleCount; row++) {
    for (let col = 0; col < moduleCount; col++) {
      if (!qr.isDark(row, col)) continue;
      ctx.fillRect(
        offset + (col + MARGIN_MODULES) * moduleSize,
        offset + (row + MARGIN_MODULES) * moduleSize,
        moduleSize,
        moduleSize,
      );
    }
  }

  const logo = await loadLogo();
  const logoSize = Math.round(size * LOGO_RATIO);
  const logoOffset = Math.round((size - logoSize) / 2);
  ctx.drawImage(logo, logoOffset, logoOffset, logoSize, logoSize);
};

/** Render off-screen at print size and hand back a PNG data URL. */
export const qrPngDataUrl = async (
  text: string,
  size: number = QR_EXPORT_SIZE,
): Promise<string> => {
  const canvas = document.createElement('canvas');
  await drawQrToCanvas(canvas, text, size);
  return canvas.toDataURL('image/png');
};
